/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Col as GridCol, Container as GridContainer, Row as GridRow } from 'react-grid-system';
import { styled } from 'styled-components';

import { createResponsiveGeneralStyle, createResponsiveGridStyle, extendChildrenWithProps } from '../../../utils';
import { Container } from '../Container';

type GridProps = {
  sidePadding?: number | number[];
  gutterWidth?: number | number[];
  gutterHeight?: number | number[];
  height?: 'auto' | '100%';
  children: React.ReactNode;
};

type GridRowProps = GridProps & {
  textAlign?: 'left' | 'center' | 'right';
};

type GridColProps = GridProps & {
  flexOrder?: number | number[];
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'flex-start' | 'center' | 'flex-end';
  horizontalAlign?: 'flex-start' | 'center' | 'flex-end';
};

const CustomContainer = styled.div<GridProps>`
  overflow-x: hidden;
  ${props => props.height && `height: ${props.height}`};
  ${props => props.sidePadding !== undefined && `${createResponsiveGridStyle('padding-left', props.sidePadding)}`};
  ${props => props.sidePadding !== undefined && `${createResponsiveGridStyle('padding-right', props.sidePadding)}`};
`;

const CustomCol = styled(Container)<GridColProps>`
  display: flex;
  flex-direction: column;
  ${props => props.gutterWidth && `${createResponsiveGridStyle('padding-left', getHalfNumber(props.gutterWidth))}`};
  ${props => props.gutterWidth && `${createResponsiveGridStyle('padding-right', getHalfNumber(props.gutterWidth))}`};
  ${props => props.gutterHeight && `${createResponsiveGridStyle('margin-bottom', props.gutterHeight)}`};
  ${props => props.flexOrder && `${createResponsiveGeneralStyle('order', props.flexOrder)}`};
  ${props => props.verticalAlign && `${createResponsiveGeneralStyle('justify-content', props.verticalAlign)}`};
  ${props => props.horizontalAlign && `${createResponsiveGeneralStyle('align-items', props.horizontalAlign)}`};
  ${props => props.textAlign && `${createResponsiveGeneralStyle('text-align', props.textAlign)}`};
`;

type CustomRowProps = {
  height?: 'auto' | '100%';
  gutterRowWidth?: number | number[];
};

const CustomRow = styled.div<CustomRowProps>`
  ${props => props.height && `height: ${props.height}`};
  ${props =>
    props.gutterRowWidth && `${createResponsiveGridStyle('margin-left', getHalfNegativeNumber(props.gutterRowWidth))}`};
  ${props =>
    props.gutterRowWidth &&
    `${createResponsiveGridStyle('margin-right', getHalfNegativeNumber(props.gutterRowWidth))}`};
`;

const half = (value: number) => value / 2;
const negate = (value: number) => -value;

const getTransformedNumber = (value: number | number[], transformationFn: any) => {
  if (typeof value === 'number') {
    return transformationFn(value);
  } else if (Array.isArray(value)) {
    return value.map(number => transformationFn(number));
  }
  return 0;
};

const getHalfNumber = (value: number | number[]) => getTransformedNumber(value, half);
const getNegativeNumber = (value: number | number[]) => getTransformedNumber(value, negate);
const getHalfNegativeNumber = (value: number | number[]) => getNegativeNumber(getHalfNumber(value));

/** @deprecated Use CssGrid (add functionality there if missing) */
export const Grid = ({ sidePadding = 0, gutterWidth, gutterHeight, height, children, ...rest }: GridProps & any) => (
  <GridContainer
    fluid
    component={CustomContainer}
    sidePadding={sidePadding}
    gutterWidth={gutterWidth}
    gutterHeight={gutterHeight}
    height={height}
    {...rest}
  >
    {extendChildrenWithProps(children, { gutterHeight, gutterWidth, height })}
  </GridContainer>
);

Grid.Row = ({ children, height, gutterWidth, gutterHeight, textAlign, ...rest }: GridRowProps & any) => (
  <GridRow component={CustomRow} {...rest} height={height} gutterRowWidth={gutterWidth}>
    {extendChildrenWithProps(children, { gutterHeight, gutterWidth, textAlign })}
  </GridRow>
);

Grid.Col = ({
  children,
  height,
  gutterWidth,
  gutterHeight,
  flexOrder,
  horizontalAlign,
  verticalAlign,
  textAlign,
  ...rest
}: GridColProps & any) => (
  <GridCol
    component={CustomCol}
    {...rest}
    height={height}
    gutterWidth={gutterWidth}
    gutterHeight={gutterHeight}
    flexOrder={flexOrder}
    horizontalAlign={horizontalAlign}
    verticalAlign={verticalAlign}
    textAlign={textAlign}
  >
    {children}
  </GridCol>
);
